import 'bpmn-js/dist/assets/diagram-js.css';
import 'bpmn-js/dist/assets/bpmn-js.css';
import './color-picker/color-picker.css';
import 'bpmn-js/dist/assets/bpmn-font/css/bpmn-embedded.css';
import { ActionIcon, Affix, Badge, Button, Divider, Group, Paper, Stack, Tabs, Text } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconEye, IconKeyboard } from '@tabler/icons-react';
import { EventBus } from 'bpmn-js/lib/BaseViewer';
import BpmnModeler from 'bpmn-js/lib/Modeler';
import ElementRegistry from 'diagram-js/lib/core/ElementRegistry';
import Overlays from 'diagram-js/lib/features/overlays/Overlays';
import { uniq } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Show } from '../../../../../react-commons/components/control-flow/Show';
import { Controlled_BooleanInput } from '../../../../../react-commons/components/inputs/controlled/Controlled_BooleanInput';
import { Controlled_BooleanSwitchInput } from '../../../../../react-commons/components/inputs/controlled/Controlled_BooleanSwitchInput';
import { OpenModal } from '../../../../../react-commons/utils/modals/openModal';
import { useActivitiesModelActions } from '../../../resource/resources/artifacts/activities/activitiesModelResource';
import {
    ContentParentRelation,
    useContentActions,
} from '../../../resource/resources/artifacts/content/contentResource';
import { RoleResource, useRoleActions } from '../../../resource/resources/artifacts/role/roleResource';
import { useUserId } from '../../authentication/useUserId';
import { ActivitiesDrawerContent } from '../../page-components/content-drawer/ActivitiesDrawerContent';
import { ContentsTableComponent } from '../../page-components/content-drawer/ContentsTableComponent';
import { RoleDrawerContent } from '../../page-components/process-area/role/RoleDrawerContent';
import { ConditionalComponent } from '../../reusable-components/ConditionalComponent';
import { LightenDarkenColor } from '../../util/utilFunctions';
import { BPMNModeler } from './BPMNModeler';
import { getLabelAttr } from './bpmn-util/getLabelAttr';
import { VersionService } from './VersionService';

export const BPMNModelerEditor = () => {
    const userId = useUserId('');
    const isAuthenticated = Boolean(userId);
    const { activitiesId } = useParams<{ activitiesId: string }>();
    const [bpmnModeler, setBPMNModeler] = useState<BpmnModeler | null>(null);
    const [loadedBpmnModeler, setLoadedBPMNModeler] = useState<BpmnModeler | null>(null);
    const [isDirty, setIsDirty] = useState<boolean>(false);
    const [opened, { open, close }] = useDisclosure(false);
    const [openedRoleDrawerContent, { open: openRoleDrawer, close: closeRoleDrawer }] = useDisclosure(false);

    const [roleFilter, { toggle: toggleRoleFilter }] = useDisclosure(true);
    const [rolesToFilter, setRolesToFilter] = useState<Array<string>>([]);
    const [selectedRole, setSelectedRole] = useState<string | null>(null);
    const [originalColorsRecord, setOriginalColorsRecord] = useState<Record<string, string>>({});

    const [activeTab, setActiveTab] = useState<string | null>('modeler');
    const [selectedElementId, setSelectedElementId] = useState<string | null>(null);
    const [selectedElementTitle, setSelectedElementTitle] = useState<string | null>(null);
    const [showMultipleRoles, setShowMultipleRoles] = useState<boolean>(false);
    const [activitiesRefObjectId, setActivitiesRefObjectId] = useState<string | undefined>(undefined);

    const activityModelActions = useActivitiesModelActions();
    const contentActions = useContentActions();

    const activityModelQuery = activityModelActions.useGetSingleResource(activitiesId);
    const contentsQuery = contentActions.useGetAllWithSectionResources(
        ContentParentRelation.ACTIVITIES_MODEL,
        activityModelQuery.data?.id
    );
    const rolesQuery = useRoleActions().useGetAllResources(activityModelQuery.data?.versionedIn);

    const updateModel = activityModelActions.useUpdateResource();

    const relevantRoles = useMemo(() => {
        const rolesList: Array<RoleResource> = [];
        contentsQuery.data?.forEach((value) => {
            value.relevantRoles.forEach((roleId) => {
                const role = rolesQuery.data?.find((value) => value.objectId === roleId);
                if (role) {
                    rolesList.push(role);
                }
            });
        });
        return uniq(rolesList);
    }, [contentsQuery.data, rolesQuery.data]);

    useEffect(() => {
        if (!activityModelQuery.data) {
            return;
        }
        if (bpmnModeler) {
            let bpmnText = activityModelQuery.data.BPMNModel;
            bpmnText = bpmnText.replace('Process_0any3sf', 'Activities');
            bpmnModeler.get<VersionService>('versionService').setVersionId(activityModelQuery.data.versionedIn);
            bpmnModeler.importXML(bpmnText).then(({ warnings }) => {
                if (warnings.length) {
                    console.warn(warnings);
                }

                // @ts-expect-error
                bpmnModeler.get('canvas').zoom('fit-viewport', 'auto');
                setLoadedBPMNModeler(bpmnModeler);
            });
        } else {
            setLoadedBPMNModeler(null);
        }
    }, [activityModelQuery.data, bpmnModeler]);

    useEffect(() => {
        const modeler = loadedBpmnModeler;
        if (rolesQuery.data && contentsQuery.data && modeler) {
            const overlays: Overlays = modeler.get('overlays');
            const elementRegistry: ElementRegistry = modeler.get('elementRegistry');

            let hasMultipleRoles = false;
            elementRegistry.forEach((element) => {
                if (
                    element.type !== 'label' &&
                    element.type !== 'bpmn:Process' &&
                    element.type !== 'bpmn:SequenceFlow'
                ) {
                    const content = contentsQuery.data?.find((value) => value.modelElement === element.id);
                    if (content) {
                        if (content.hasContentSections) {
                            overlays.add(element.id, {
                                position: {
                                    top: 0,
                                    right: -4,
                                },
                                html: `<span class="custom-badge">C</span>`,
                            });
                        }
                        const gfx = elementRegistry.getGraphics(element);
                        const visual = gfx.querySelector('.djs-visual');
                        const shape = visual?.firstElementChild;
                        if (roleFilter && relevantRoles.length > 0) {
                            const filteredRoles = content.relevantRoles.filter((roleId) =>
                                rolesToFilter.includes(roleId)
                            );
                            const roles =
                                rolesQuery.data?.filter((value) => filteredRoles.includes(value.objectId)) ?? [];
                            const color =
                                roles.length > 1 ? '#215967'
                                : roles.length === 1 ? roles[0].color
                                : 'gray';
                            if (roles.length > 1) {
                                hasMultipleRoles = true;
                            }

                            if (shape) {
                                const styleAttribute = shape.getAttribute('style');
                                if (styleAttribute) {
                                    if (!(element.id in originalColorsRecord)) {
                                        const record = originalColorsRecord;
                                        record[element.id] = styleAttribute;
                                        setOriginalColorsRecord(record);
                                    }
                                    shape.setAttribute(
                                        'style',
                                        styleAttribute
                                            .replace(
                                                /fill:[^;]+;/,
                                                `fill: ${LightenDarkenColor(color, roles.length > 1 ? 75 : 150)};`
                                            )
                                            .replace(/stroke:[^;]+;/, `stroke: ${color};`)
                                    );
                                }
                                shape.setAttribute('fill', LightenDarkenColor(color, roles.length > 1 ? 75 : 150));
                                shape.setAttribute('stroke', color);
                            }
                        } else {
                            const originalColor = originalColorsRecord[element.id];
                            if (originalColor && shape) {
                                shape.setAttribute('style', originalColor);
                            }
                        }
                    }
                }
            });
            setShowMultipleRoles(hasMultipleRoles);
        }
    }, [contentsQuery.data, rolesQuery.data, loadedBpmnModeler, roleFilter, rolesToFilter]);

    const handleSave = () => {
        const data = activityModelQuery.data;
        if (data) {
            loadedBpmnModeler?.saveXML({ format: true }).then(({ xml, error }) => {
                if (error) {
                    notifications.show({
                        color: 'var(--mantine-color-error)',
                        title: 'Invalid BPMN Model',
                        message: `${error.name} : ${error.message}`,
                        withCloseButton: true,
                        withBorder: true,
                    });
                    return;
                }

                updateModel
                    .mutateAsync({
                        resource: {
                            ...data,
                            BPMNModel: xml ?? '',
                        },
                        id: data.id,
                    })
                    .then((value) => {
                        if (value?.successful) {
                            setIsDirty(false);
                        }
                    });
            });
        }
    };

    const handleReset = () => {
        OpenModal.AlertModal(
            'Warning',
            `Are you want sure you want to reset model to last saved state? All changes will be lost!`,
            () => {
                loadedBpmnModeler?.importXML(activityModelQuery.data?.BPMNModel ?? '').then(({ warnings }) => {
                    if (warnings.length) {
                        console.warn(warnings);
                    }

                    // @ts-expect-error
                    loadedBpmnModeler?.get('canvas').zoom('fit-viewport');
                    setIsDirty(false);
                });
            }
        );
    };

    const handleExit = () => {
        if (isDirty) {
            OpenModal.AlertModal(
                'Warning',
                `Are you want sure you want to exit edit mode? All unsaved changes will be lost!`,
                () => {
                    window.history.back();
                }
            );
        } else {
            window.history.back();
        }
    };

    useEffect(() => {
        const eventBus: EventBus<any> | undefined = loadedBpmnModeler?.get('eventBus');
        const overlays = loadedBpmnModeler?.get('overlays');

        const contextMenuClickHandler = (event: any, localIsDirty: boolean) => {
            event.originalEvent.preventDefault();
            event.originalEvent.stopPropagation();
            if (
                event.element.type === 'label' ||
                event.element.type === 'bpmn:Process' ||
                event.element.type === 'bpmn:SequenceFlow'
            ) {
                return;
            }
            if (localIsDirty) {
                OpenModal.AlertModal(
                    'Warning',
                    `Unsaved changes in the model! Please save before trying to edit content!`,
                    () => {
                        handleSave();
                        const element = event.element;
                        if (element.id.includes('Activities_Reference')) {
                            setActivitiesRefObjectId(element.businessObject.objectId);
                        } else {
                            setActivitiesRefObjectId(undefined);
                        }
                        setSelectedElementId(element.id);
                        const labelAttr = getLabelAttr(element.businessObject);
                        if (labelAttr) {
                            const label =
                                labelAttr === 'categoryValueRef' ?
                                    element.businessObject.categoryValueRef.value
                                :   element.businessObject[labelAttr];
                            setSelectedElementTitle(label);
                        } else {
                            setSelectedElementTitle(null);
                        }
                    },
                    undefined,
                    'Save Model'
                );
            } else {
                const element = event.element;
                if (element.id.includes('Activities_Reference')) {
                    setActivitiesRefObjectId(element.businessObject.objectId);
                } else {
                    setActivitiesRefObjectId(undefined);
                }
                setSelectedElementId(element.id);
                const labelAttr = getLabelAttr(element.businessObject);
                if (labelAttr) {
                    const label =
                        labelAttr === 'categoryValueRef' ?
                            element.businessObject.categoryValueRef.value
                        :   element.businessObject[labelAttr];
                    setSelectedElementTitle(label);
                } else {
                    setSelectedElementTitle(null);
                }
            }
        };

        const changedElementHandler = (currentModel: string) => {
            bpmnModeler?.saveXML({ format: true }).then(({ xml, error }) => {
                if (error) {
                    setIsDirty(true);
                }
                setIsDirty(Boolean(currentModel !== xml));
            });
        };

        eventBus?.on('element.hover', (event) => {
            if (
                event.element.type === 'label' ||
                event.element.type === 'bpmn:Process' ||
                event.element.type === 'bpmn:SequenceFlow'
            ) {
                return;
            }
            // @ts-expect-error
            const overlayId = overlays?.add(event.element.id, {
                position: {
                    bottom: 0,
                    right: 0,
                },
                html: `<div>${event.element.id}</div>`,
            });
            eventBus?.once('element.out', () => {
                // @ts-expect-error
                overlays?.remove(overlayId);
            });
        });

        eventBus?.on('element.contextmenu', 1500, (event) => contextMenuClickHandler(event, isDirty));
        eventBus?.on('commandStack.changed', () => changedElementHandler(activityModelQuery.data?.BPMNModel ?? ''));

        eventBus?.on('element.dblclick', 1500, function (context) {
            const element = context.element;
            if (element.type == 'bpmn:Task' && element.id.includes('Reference')) {
                return false;
            }
        });

        return () => {
            eventBus?.off('element.contextmenu');
            eventBus?.off('commandStack.changed');
            eventBus?.off('element.hover');
        };
    }, [loadedBpmnModeler, activityModelQuery.data, isDirty]);

    useEffect(() => {
        const eventBus: EventBus<any> | undefined = loadedBpmnModeler?.get('eventBus');

        const keyboardCallback = () => {
            if (opened) {
                return false;
            }
        };

        eventBus?.on('keyboard.keydown', 1500, keyboardCallback);
        eventBus?.on('keyboard.keyup', 1500, keyboardCallback);
        return () => {
            eventBus?.off('keyboard.keydown', keyboardCallback);
            eventBus?.off('keyboard.keyup', keyboardCallback);
        };
    }, [loadedBpmnModeler, opened]);

    useEffect(() => {
        if (selectedElementId) {
            open();
            closeRoleDrawer();
        }
    }, [selectedElementId]);

    useEffect(() => {
        if (contentsQuery.data) {
            const rolesToUpdate: Array<string> = [];
            contentsQuery.data.forEach((content) => {
                rolesToUpdate.push(...content.relevantRoles);
            });
            setRolesToFilter(uniq(rolesToUpdate));
        }
    }, [contentsQuery.data]);

    return (
        <div style={{ height: '100%' }}>
            <ConditionalComponent label={'Please login as an Admin to view BPMN Editor'} condition={isAuthenticated}>
                <ActivitiesDrawerContent
                    openedDrawer={opened}
                    closeDrawer={() => {
                        setSelectedElementId(null);
                        setSelectedElementTitle(null);
                        close();
                    }}
                    activitiesRefObjectId={activitiesRefObjectId}
                    activitiesModelId={activityModelQuery.data?.id ?? ''}
                    elementId={selectedElementId ?? ''}
                    elementTitle={selectedElementTitle}
                    contentResource={
                        contentsQuery.data?.find((value) => value.modelElement === selectedElementId) ?? null
                    }
                    readOnly={false}
                />
                <RoleDrawerContent
                    closeDrawer={() => {
                        setSelectedRole(null);
                        closeRoleDrawer();
                    }}
                    openedDrawer={openedRoleDrawerContent}
                    roleId={selectedRole ?? ''}
                />
                <Tabs
                    h={'100%'}
                    w={'100%'}
                    value={activeTab}
                    onChange={(value) => {
                        if (value === 'contents' && isDirty) {
                            OpenModal.AlertModal(
                                'Warning',
                                `Unsaved changes in the model! Please save before trying to edit content!`,
                                () => {
                                    handleSave();
                                    setActiveTab(value);
                                },
                                undefined,
                                'Save Model'
                            );
                        } else {
                            setActiveTab(value);
                        }
                    }}
                    style={{ flexGrow: 1 }}
                >
                    <Tabs.List>
                        <Tabs.Tab value={'modeler'}>Modeler</Tabs.Tab>
                        <Tabs.Tab value={'contents'}>Contents</Tabs.Tab>
                    </Tabs.List>
                    <Tabs.Panel h={'calc(100% - 35px)'} w={'100%'} value={'modeler'}>
                        <BPMNModeler
                            setBPMNModeler={setBPMNModeler}
                            bpmnModelerNeeded={bpmnModeler === null}
                            versionId={activityModelQuery.data?.versionedIn ?? ''}
                        />
                        <Show when={!opened && !openedRoleDrawerContent && activeTab === 'modeler'}>
                            <Affix position={{ top: 90, right: 15 }}>
                                <ActionIcon
                                    onClick={() => {
                                        OpenModal.ContentModal({
                                            title: 'Keyboard Shortcuts',
                                            content: (
                                                <Group w={350}>
                                                    <Stack>
                                                        <Text>Undo</Text>
                                                        <Text>Redo</Text>
                                                        <Text>Select All</Text>
                                                        <Text>Zoom</Text>
                                                        <Text>Scrolling (Horizontal)</Text>
                                                        <Text>Hand Tool</Text>
                                                        <Text>Lasso Tool</Text>
                                                        <Text>Space Tool</Text>
                                                    </Stack>
                                                    <div style={{ flexGrow: 1 }}></div>
                                                    <Stack align="flex-end">
                                                        <Text>ctrl + Z</Text>
                                                        <Text>ctrl + ⇧ + Z</Text>
                                                        <Text>ctrl + A</Text>
                                                        <Text>ctrl + Scrolling</Text>
                                                        <Text>⇧ + Scrolling</Text>
                                                        <Text>H</Text>
                                                        <Text>L</Text>
                                                        <Text>S</Text>
                                                    </Stack>
                                                </Group>
                                            ),
                                        });
                                    }}
                                    variant={'default'}
                                    color={'var(--mantine-color-base-button)'}
                                >
                                    <IconKeyboard />
                                </ActionIcon>
                            </Affix>
                            {relevantRoles.length > 0 && (
                                <Affix position={{ top: 130, right: 15 }}>
                                    <Paper
                                        mah={400}
                                        withBorder={true}
                                        p={'sm'}
                                        shadow="lg"
                                        style={{ overflowY: 'auto' }}
                                    >
                                        <Stack>
                                            <Group>
                                                <Controlled_BooleanSwitchInput
                                                    value={roleFilter}
                                                    onChange={toggleRoleFilter}
                                                    readOnly={false}
                                                />
                                                <Text w={'100%'} style={{ textAlign: 'center' }}>
                                                    Relevant Roles
                                                </Text>
                                            </Group>
                                            <Divider />
                                            <Controlled_BooleanInput
                                                nullable={false}
                                                value={relevantRoles.length === rolesToFilter.length}
                                                onChange={() => {
                                                    if (relevantRoles.length === rolesToFilter.length) {
                                                        setRolesToFilter([]);
                                                    } else {
                                                        setRolesToFilter(relevantRoles.map((value) => value.objectId));
                                                    }
                                                }}
                                                readOnly={false}
                                                labelPosition={'right'}
                                                label={'Select All'}
                                            />
                                            {relevantRoles.map((role) => (
                                                <Group key={role.objectId}>
                                                    <Controlled_BooleanInput
                                                        nullable={false}
                                                        value={rolesToFilter.includes(role.objectId)}
                                                        onChange={() => {
                                                            if (rolesToFilter.includes(role.objectId)) {
                                                                setRolesToFilter(
                                                                    rolesToFilter.filter(
                                                                        (value) => value !== role.objectId
                                                                    )
                                                                );
                                                            } else {
                                                                setRolesToFilter([...rolesToFilter, role.objectId]);
                                                            }
                                                        }}
                                                        readOnly={false}
                                                    />
                                                    <Badge
                                                        variant={'filled'}
                                                        color={LightenDarkenColor(role.color, 150)}
                                                        style={{
                                                            border: 2,
                                                            borderStyle: 'solid',
                                                            borderColor: role.color,
                                                        }}
                                                        fullWidth
                                                        autoContrast
                                                    >
                                                        {role.name}
                                                    </Badge>
                                                    <ActionIcon
                                                        size={'sm'}
                                                        onClick={() => {
                                                            setSelectedRole(role.id);
                                                            close();
                                                            openRoleDrawer();
                                                        }}
                                                        variant={'transparent'}
                                                        color={'var(--mantine-color-base-button)'}
                                                        disabled={false}
                                                    >
                                                        <IconEye />
                                                    </ActionIcon>
                                                </Group>
                                            ))}
                                            <Show when={showMultipleRoles}>
                                                <Divider />
                                                <Badge
                                                    variant={'filled'}
                                                    color={LightenDarkenColor('#215967', 75)}
                                                    style={{
                                                        border: 2,
                                                        borderStyle: 'solid',
                                                        borderColor: '#215967',
                                                    }}
                                                    fullWidth
                                                    autoContrast
                                                >
                                                    Multiple Roles
                                                </Badge>
                                            </Show>
                                        </Stack>
                                    </Paper>
                                </Affix>
                            )}
                            <Affix position={{ bottom: 30, right: 80 }}>
                                <Group justify={'flex-end'}>
                                    <Button onClick={handleExit} variant={'filled'}>
                                        Exit
                                    </Button>
                                    <Button onClick={handleReset} variant={'filled'} disabled={!isDirty}>
                                        Reset
                                    </Button>
                                    <Button onClick={handleSave} variant={'filled'} disabled={!isDirty}>
                                        Save
                                    </Button>
                                </Group>
                            </Affix>
                            <Affix position={{ bottom: 25, left: 20 }}>
                                <Paper withBorder={true} p={'sm'} shadow="lg">
                                    <Text>Hint: Right-Click on Element to create/edit Content</Text>
                                </Paper>
                            </Affix>
                        </Show>
                    </Tabs.Panel>
                    <Tabs.Panel h={'calc(100% - 35px)'} w={'100%'} value={'contents'}>
                        <ContentsTableComponent
                            activitiesModelId={activityModelQuery.data?.id ?? ''}
                            contentsQuery={contentsQuery}
                            openDrawerAtElement={(elementId) => setSelectedElementId(elementId)}
                        />
                    </Tabs.Panel>
                </Tabs>
            </ConditionalComponent>
        </div>
    );
};
